import React, { useRef } from 'react'
import * as S from './FeaturesSection.styled'
import { Clock, Earth, Secure } from '@enuygun/icons'
import SnapCarousel from 'components/SnapCarousel'
import { useIsMobile } from 'hooks/useIsMobile'
import { useAppState } from 'context/AppContext'
import { i18n, useTranslation } from 'next-i18next'
// Körfez dışı: 3. madde Secure + FeaturesSection.items (i18n). Körfez (sa-ar / ae-ar / kw-ar) ayrıca override edilir.
const defaultIcons = [<Earth key="earth" height={40} width={40} />, <Clock key="clock" height={40} width={40} />, <Secure key="secure" height={40} width={40} />]

// Gulf markets payment methods configuration
const GULF_PAYMENT_METHODS: Record<string, string[]> = {
  'sa-ar': ['MADA', 'Tabby', 'Tamara'],
  'ae-ar': ['Tabby', 'Tamara'],
  'kw-ar': ['KNET'],
}

// Başlık + açıklama metninde ödeme adları: locales/*/common.json → FeaturesSection.gulfPayment (i18n {{methods}})

// Logo görselleri: CDN (Körfez ödeme yöntemleri)
const GULF_METHOD_LOGOS: Record<string, { src: string; alt: string; width: number; height: number }> = {
  Tamara: { src: 'https://cdn2.enuygun.com/media/lib/uploads/image/tamara-72952.png', alt: 'Tamara', width: 50, height: 20 },
  Tabby: { src: 'https://cdn2.enuygun.com/media/lib/uploads/image/tabby-72953.png', alt: 'Tabby', width: 50, height: 20 },
  KNET: { src: 'https://cdn2.enuygun.com/media/lib/uploads/image/knet-72954.png', alt: 'K-NET', width: 50, height: 40 },
  MADA: { src: 'https://cdn2.enuygun.com/media/lib/uploads/image/mada-72955.png', alt: 'Mada', width: 50, height: 20 },
}

// Payment methods badge component (Körfez: logolar dikey, doğal yükseklik)
const PaymentMethodsBadge = ({ methods }: { methods: string[] }) => (
  <S.PaymentLogosRow>
    {methods.map((method) => {
      const meta = GULF_METHOD_LOGOS[method]
      if (!meta) return null
      return (
        // Marka vektör SVG'leri: next/image + custom loader bu kullanım için uygun değil
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={method}
          src={meta.src}
          alt={meta.alt}
          width={meta.width}
          height={meta.height}
          loading="lazy"
          decoding="async"
          style={{
            display: 'block',
            width: `${meta.width}px`,
            height: `${meta.height}px`,
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
      )
    })}
  </S.PaymentLogosRow>
)

// App'te market: 'sa' | 'ae' | 'kw' (Markets) — GULF config ise path locale: 'sa-ar' vb. (AppState.fullLocale)
const getGulfPaymentLocaleId = (fullLocale?: string, market?: string, language?: string): string | null => {
  if (fullLocale && fullLocale in GULF_PAYMENT_METHODS) return fullLocale
  if (market && language) {
    const id = `${market}-${language}`
    if (id in GULF_PAYMENT_METHODS) return id
  }
  return null
}

// Get payment methods for Gulf path locale (örn. 'sa-ar')
const getPaymentMethodsForLocale = (gulfLocaleId: string | undefined): string[] => {
  if (!gulfLocaleId) return []
  return GULF_PAYMENT_METHODS[gulfLocaleId] || []
}

// Format payment methods list for Arabic description
// "MADA, Tabby, Tamara" -> "مدى، تابي، تمارة"
const formatPaymentMethodsArabic = (methods: string[]): string => {
  const arabicNames: Record<string, string> = {
    MADA: 'مدى',
    Tabby: 'تابي',
    Tamara: 'تمارة',
    KNET: 'كي-نت',
  }

  const translated = methods.map((m) => arabicNames[m] || m)
  return translated.join('، ')
}

function FeaturesSection() {
  const isMobile = useIsMobile()
  const localeLanguage = i18n?.language
  const isRTL = i18n?.dir(localeLanguage?.includes('-') ? localeLanguage.split('-')[1] : localeLanguage) === 'rtl'
  const { market, fullLocale, language } = useAppState()
  const { t } = useTranslation('common')

  const featuresSectionContainerRef = useRef<HTMLDivElement>(null)

  // Get feature items from translations
  const featureItems = t('FeaturesSection.items', { returnObjects: true }) as Array<{ title: string; link: string }> | undefined

  const gulfLocaleId = getGulfPaymentLocaleId(fullLocale, market, language)

  // Build features array
  const buildFeatures = () => {
    const baseFeatures = Array.isArray(featureItems)
      ? featureItems.map((item, index) => ({
          icon: defaultIcons[index] || null,
          title: item.title,
          description: item.link,
        }))
      : []

    // Körfez: path locale (ör. sa-ar) + ödeme yöntemleri; market tek başına 'sa' olduğu için fullLocale gerekir
    if (gulfLocaleId && baseFeatures.length >= 3) {
      const paymentMethods = getPaymentMethodsForLocale(gulfLocaleId)
      const methodsText = formatPaymentMethodsArabic(paymentMethods)

      const paymentFeature = {
        icon: defaultIcons[2],
        title: t('FeaturesSection.gulfPayment.title'),
        description: t('FeaturesSection.gulfPayment.description', { methods: methodsText }),
        logos: <PaymentMethodsBadge methods={paymentMethods} />,
      }

      // Ödeme yöntemleri kartını en başa al: [Payment, Earth, Clock]
      return [paymentFeature, baseFeatures[0], baseFeatures[1]]
    }

    return baseFeatures
  }

  const features = buildFeatures()

  const renderFeatures = () =>
    features.map((feature, index) => (
      <S.FeatureItem isMobile={isMobile} key={feature.title} data-testid={`feature-item-${index}`}>
        <S.FeatureIcon data-testid={`feature-item-icon-${index}`}>{feature.icon}</S.FeatureIcon>
        <S.FeatureContent>
          <S.FeatureTitle data-testid={`feature-item-title-${index}`}>{feature.title}</S.FeatureTitle>
          <S.FeatureDescription data-testid={`feature-item-description-${index}`}>{feature.description}</S.FeatureDescription>
          {'logos' in feature ? feature.logos : null}
        </S.FeatureContent>
      </S.FeatureItem>
    ))

  return (
    <S.FeaturesSectionContainer ref={featuresSectionContainerRef} isRTL={isRTL} isMobile={isMobile}>
      <S.FeaturesSectionWrapper>
        {isMobile ? (
          <SnapCarousel
            options={{
              perPage: 1.2,
              gap: 16,
              hasLastChildPadding: true,
              paddingCarouselCard: 2,
            }}
            isRTL={isRTL}
            dataTestId="advert-carousel"
            containerWidth={1120}
            isMobile={true}
          >
            {renderFeatures()}
          </SnapCarousel>
        ) : (
          renderFeatures()
        )}
      </S.FeaturesSectionWrapper>
    </S.FeaturesSectionContainer>
  )
}

export default FeaturesSection
